import { VideoPlayerInterface } from '@cbsinteractive/avia-js';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { AviaPlayerDaiApiAdHook } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerDaiApiAdHook';
import { AviaPlayerFairPlayRetryHook } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerFairPlayRetryHook';
import { AviaPlayerInjectedAdHook } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerInjectedAdHook';
import { AviaPlayerPreferredVideoCodecHook } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerPreferredVideoCodecHook';
import { AviaPlayerPremiumAudioHook } from 'tag/ts/tag/player/adapters/avia/hooks/AviaPlayerPremiumAudioHook';
import { IAviaPlayerHooksManagerArgument } from 'tag/ts/tag/player/adapters/avia/interfaces/arguments/IAviaPlayerHooksManagerArgument';
import { IAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlayerHook';

/**
 * @copyright © Paramount Streaming
 * @author  Zeeshan Ahmad
 * @description VTG Avia player hook system manager for Smart Tag integration
 *
 * **VTG Hook System Integration:**
 * Manages VTG Avia player's hook system that allows Smart Tag to inject custom logic
 * into specific VTG player events and modify player data in real-time. Hooks enable
 * Smart Tag to customize VTG player behavior without modifying VTG core code.
 *
 * **Hook Types & Functions:**
 * - **Ad Hooks**: Modify ad behavior (skip functionality, ad injection)
 * - **Audio Hooks**: Handle premium audio codec preferences and channel configuration
 * - **Video Hooks**: Manage preferred video codec selection for optimal playback
 *
 * **Hook Lifecycle Management:**
 * - **Registration**: Responds to ON_PLAYER_HOOK_REQUEST_REGISTER events
 * - **Removal**: Handles ON_PLAYER_HOOK_REQUEST_REMOVE events
 * - **Cleanup**: Automatically removes hooks during player teardown
 * - **Event Coordination**: Manages hook communication via Smart Tag event system
 *
 * **Managed Hook Implementations:**
 * - **AviaPlayerDaiApiAdHook**: DAI ad stream management for live content
 * - **AviaPlayerInjectedAdHook**: Custom ad skipping logic via callbacks
 * - **AviaPlayerPreferredVideoCodecHook**: Video codec optimization for HTML5 platforms
 * - **AviaPlayerPremiumAudioHook**: Audio codec handling for premium content
 * - **AviaPlayerFairPlayRetryHook**: FairPlay DRM key request retry resilience
 *
 * **Business Impact:**
 * Enables Smart Tag to customize VTG player behavior for specific business requirements
 * like ad skipping policies, premium audio support, and platform-specific optimizations
 * without requiring VTG player modifications.
 */
export class AviaPlayerHooksManager extends AbstractTagClass {

	#adaptedPlayerInstance: VideoPlayerInterface;

	// contains all player hooks
	#activePlayerHooks: IAviaPlayerHook[] = [];

	constructor( argument: IAviaPlayerHooksManagerArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerHooksManager'
			}
		);
		this.#adaptedPlayerInstance = argument?.player;

		this.tagEventsHandler(
			this.#onTagEvent,
			[
				TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER,
				TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE
			]
		);

		this.#initAdHook();

		// check if preferred codec hook feature is enabled
		if ( this?.tag?.model?.config?.tag?.Hooks?.params?.PreferredVideoCodecHook?.enabled === true && this?.tag?.params?.SYSTEM?.embedType === EmbedType.HTML5 ) {
			new AviaPlayerPreferredVideoCodecHook( { tag: argument?.tag } );
		}

		// check if premium audio hook feature is enabled
		this?.logger?.log( '[PLSTVVID-3370 debug] tag config:', this?.tag?.model?.config?.tag );
		if ( this?.tag?.model?.config?.tag?.Hooks?.params?.PremiumAudioHook?.enabled === true ) {
			this?.logger?.log( '[PLSTVVID-3370 debug] Premium Audio Hook is enabled in TAG config' );
			// if the user is an HTML5 user and has an audio codec preference, make sure that the premium audio codec has all the necessary channels
			const preferredAudioCodecs = argument.tag?.params?.CONTENT?.resource?.playback?.preferredAudioCodecs || [];
			if ( this.utils.Type.isArray( preferredAudioCodecs ) && !this.utils.Type.isEmpty( preferredAudioCodecs ) ) {
				this?.logger?.log( '[PLSTVVID-2137 debug] User has audio preference, add hook' );
				new AviaPlayerPremiumAudioHook( { tag: argument?.tag } );
			} else {
				this?.logger?.log( '[PLSTVVID-2137 debug] User has no audio preference, not adding hook' );
			}
		} else {
			this?.logger?.log( '[PLSTVVID-3370 debug] Premium Audio Hook not enabled in feature flags' );
		}

		// check if FairPlay license request retry hook feature is enabled
		if ( this?.tag?.model?.config?.tag?.Hooks?.params?.FairPlayRetryHook?.enabled === true ) {
			new AviaPlayerFairPlayRetryHook( { tag: argument?.tag } );
		}
	}

	/**
	 * @description Initializes appropriate ad hooks based on content and configuration
	 *
	 * **Ad Hook Selection Logic:**
	 * Determines which ad hooks to activate based on Smart Tag configuration and content type.
	 * Different ad hooks handle different ad integration scenarios for VTG player.
	 *
	 * **Hook Selection Process:**
	 * 1. **Custom Callback Check**: If isSkippable callback provided, use AviaPlayerInjectedAdHook
	 * 2. **DAI Content Check**: If DAI ad setup detected, use AviaPlayerDaiApiAdHook
	 * 3. **Configuration Fallback**: No hooks if no specific ad requirements detected
	 *
	 * **Ad Hook Types:**
	 * - **InjectedAdHook**: Custom ad skipping logic via Smart Tag callbacks
	 * - **DaiApiAdHook**: DAI (Dynamic Ad Insertion) management for live streams
	 *
	 * **Business Impact:**
	 * Enables content-specific ad policies and custom ad interaction behavior
	 * while maintaining VTG player compatibility.
	 */
	#initAdHook = (): void => {
		const adSetupInfo: IAdSetupInfo = this.tag.model.adUtilities.adSetupInfo();
		this?.logger?.log( 'initAdHook', 'adSetupInfo:', adSetupInfo );

		// if callback is passed, init ad hook for injected callbacks
		if ( !this.utils.Type.isEmpty( this?.callbacks?.isSkippable ) ) {
			new AviaPlayerInjectedAdHook( { tag: this.tag, isSkippableCallback: this?.callbacks?.isSkippable } );
			return;
		}

		// when using DAI API, SmartTag must register a hook with avia and determine an ad's skippability from the returned adItem
		// reference: https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/dai_ad_skip#dai-api-customizing-skip-promo
		if ( adSetupInfo?.setup?.definition === AdSetupDefinition.DAI_API ) {
			new AviaPlayerDaiApiAdHook( { tag: this.tag } );
			return;
		}

		// NOTE:
		// when using DAI SDK, avia player determines if ads are skippable using the SDK internally and displays its own skip button, there should be no involvement from SmartTag
		// reference: https://avia.vtg.paramount.tech/avia-js/latest/docs/guides/dai_ad_skip#dai-sdk-the-simple-case
	};

	/**
	 * @description Tag application event handler
	 * @param {ITagEventVO} event
	 */
	#onTagEvent = ( event: ITagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER:
				this?.logger?.log( this.name, TagLoggerString.ON_TAG_EVENT, event?.type, event );
				this.addHook( event?.data?.playerHookRequestRegister?.hook );
				break;
			case TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE:
				this.removeHook( event?.data?.playerHookRequestRemove?.hook );
				break;
		}
	};

	addHook = ( hook: IAviaPlayerHook ): void => {
		// sanity check
		if ( this.utils.Type.isEmpty( hook ) || !this.#adaptedPlayerInstance ) {
			this?.logger?.warn( 'addHook', 'missing/invalid params or player unavailable', 'hook:', hook, 'player:', this.#adaptedPlayerInstance );
			return;
		}

		this?.logger?.log( 'registering hook with avia player', '\ntype:', hook.hookType, '\nhook:', hook, '\nplayer:', this.#adaptedPlayerInstance );

		try {
			this.#adaptedPlayerInstance.registerHook( hook.hookType, hook.callback );
			this.#activePlayerHooks.push( hook );

			// notify app
			this.dispatchTagEvent(
				this.getTagEventVO(
					TagEvent.ON_PLAYER_HOOK_REGISTERED,
					this.tag,
					{
						player: this?.tag?.controller?.playerManager?.player,
						adaptedPlayerInstance: this.#adaptedPlayerInstance,
						hook: hook
					},
					this
				)
			);
		} catch ( error ) {
			this?.logger?.warn( 'addHook failed:', error );
		}
	};

	removeHook = ( hook: IAviaPlayerHook ): void => {
		// sanity check
		if ( this.utils.Type.isEmpty( hook ) || !this.#adaptedPlayerInstance ) {
			this?.logger?.warn( 'removeHook', 'missing/invalid params or player unavailable', 'hook:', hook, 'player:', this.#adaptedPlayerInstance );
			return;
		}

		// check if previously added
		const index = this.#activePlayerHooks.indexOf( hook );
		if ( index < 0 ) {
			this?.logger?.warn( 'removeHook', 'hook not found:', hook );
			return;
		}

		// remove
		this?.logger?.log( 'removing hook from avia player', '\ntype:', hook.hookType, '\nhook:', hook, '\nplayer:', this.#adaptedPlayerInstance );

		try {
			this.#adaptedPlayerInstance.removeHook( hook.hookType, hook.callback );
			this.#activePlayerHooks.splice( index, 1 );

			// notify app
			this.dispatchTagEvent(
				this.getTagEventVO(
					TagEvent.ON_PLAYER_HOOK_REMOVED,
					this.tag,
					{
						player: this?.tag?.controller?.playerManager?.player,
						adaptedPlayerInstance: this.#adaptedPlayerInstance,
						hook: hook
					},
					this
				)
			);
		} catch ( error ) {
			this?.logger?.warn( 'removeHook failed:', error );
		}
	};

	/**
	 * @description Run cleanup logic for all player hooks
	 */
	cleanUp = (): Promise<IPromiseResolveGeneric> => {
		return new Promise(
			( resolve, reject ) => {
				// cleanup hooks
				this.#activePlayerHooks.filter(
					( hook: IAviaPlayerHook ) => !this.utils.Type.isEmpty( hook?.cleanUp )
				).forEach(
					( hook: IAviaPlayerHook ) => {
						hook.cleanUp();
						hook = undefined;
					}
				);

				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[
						TagEvent.ON_PLAYER_HOOK_REQUEST_REGISTER,
						TagEvent.ON_PLAYER_HOOK_REQUEST_REMOVE
					]
				).then(
					( data ) => {
						resolve( { msg: `${this.name}: cleanUp is done!`, success: true } );
					}
				);
			}
		);
	};
}